import React, { useEffect, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import Slider from 'react-slick'

import { useNavigate } from 'react-router-dom'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'

import CharacterTile from './character-tile'
import anime from 'animejs'
import CharacterCarouselTile from './character-carousel-tile'
import { setMusicSource } from '../../reducers/sound'
import CarouselNextArrow from '../elements/carousel-next-arrow'
import CarouselPrevArrow from '../elements/carousel-prev-arrow'
import { ReactComponent as Squiggle } from '../../assets/svgs/squiggle-01.svg'

const mapStateToProps = (state) => {
    const { nav, characters, ui } = state
    return {
        navState: nav,
        murphys: characters,
        uiHeight: ui.height,
        uiDeviceType: ui.deviceType
    }
}

const MurphysIndex = ({ dispatch, navState, murphys, uiDeviceType, uiHeight }) => {
    const [activeCharacterId, setActiveCharacterId] = useState(null)
    const [activeSlide, setActiveSlide] = useState(0)
    const [manualChange, setManualChange] = useState(false)
    const [isUnloading, setIsUnloading] = useState(false)
    const componentName = 'Murphys'
    const navigate = useNavigate()
    let sliderRef = useRef(null)

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])
    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
            if (navState.theme !== 'grey') {
                dispatch(saveNavTheme('grey'))
            }
        } else if (componentState === 'ANIMATING_IN') {
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT_STAGED') {
            setIsUnloading(true)
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (activeCharacterId) {
            // jump to the person
            let idx = murphys.findIndex(murphy => murphy.uuid === activeCharacterId)
            sliderRef.slickGoTo(idx, true)
            setManualChange(true)
            setActiveSlide(idx)
        } else {
            // hide the slider....
        }
    }, [activeCharacterId])

    const handlePreloadingState = async () => {
        if (isUnloading) {
            setIsUnloading(false)
        }
        dispatch(fetchApiData(apiResourceEndpoint('murphys', 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        if ((uiDeviceType === 'mobile' || uiDeviceType === 'tablet') && (!activeCharacterId && activeSlide > -1)) {
            let activeMurphy = murphys[0]
            setActiveCharacterId(activeMurphy.uuid)
        }
        dispatch(setMusicSource('https://api.wayouttanoway.com/sites/default/files/2025-04/jalenConcept.mp3'))
        dispatch(completeAnimatingIn())
    }

    const handleAnimatingOutState = async () => {
        const slideUp = await animateOutCharacter()
        slideUp.finished.then(() => {
            setActiveCharacterId(null)
            setIsUnloading(true)
            dispatch(completeAnimatingOut())
        })
    }

    const animateOutCharacter = async () => {
        return anime({
            targets: '.story-swipe',
            keyframes: [
                { top: -1 * uiHeight }
            ],
            duration: 500,
            easing: 'easeInOutQuad'
        })
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        if (activeCharacterId) {
            setActiveCharacterId(null)
            setActiveSlide(0)
        }
        return null
    }

    const handleCharacterClick = (characterId) => {
        if (characterId === activeCharacterId) {
            setActiveCharacterId(null)
        } else {
            setActiveCharacterId(characterId)
        }
    }

    const renderMurphys = () => {
        let allMurphys = []
        murphys.forEach(murphy => {
            allMurphys.push(
                <CharacterTile
                    key={murphy.uuid}
                    character={murphy}
                    parentComponentState={componentState}
                    activeCharacterId={activeCharacterId}
                    onHandleCharacterClick={handleCharacterClick} />)
        })
        return allMurphys
    }

    const handleStoryClick = () => {
        const murphy = murphys.find(m => m.uuid === activeCharacterId)
        let slideUp = anime.timeline({
            duration: 450,
            easing: 'easeInOutQuad'
        })
            .add({
                targets: '.story-swipe',
                top: 0
            })
            .add({
                targets: '.murphy-carousel-container',
                opacity: 0
            }, '+=100')
            .add({
                targets: '.character-swipe-name',
                opacity: 1
            }, '+=200')
            .add({
                targets: '.title-stroke svg path',
                strokeDashoffset: 0
            }, '+=200')
        slideUp.finished.then(() => {
            const murphyRoute = buildRoutePath(routes.murphysView.path, { character: murphy.alias.substring(1) })
            // wait 500sm before navigating
            setTimeout(() => {
                navigate(murphyRoute)
            }, 500)
        })
    }

    const renderMurphysCarousel = () => {
        let allMurphys = []
        murphys.forEach(murphy => {
            allMurphys.push(
                <CharacterCarouselTile
                    key={murphy.uuid}
                    character={murphy}
                    handleStoryClick={handleStoryClick}/>)
        })
        return allMurphys
    }

    const renderCharacterStartButton = () => {
        if (activeCharacterId) {
            let activeMurphy = murphys[activeSlide]
            return (
                <div className="start-cta">
                    <button className="btn" onClick={handleStoryClick}>
                        Discover {activeMurphy.firstName}'s Story
                    </button>
                </div>
            )
        }
        return null
    }

    const renderStorySwipe = () => {
        const murphy = murphys.find(m => m.uuid === activeCharacterId)
        if (murphy && activeCharacterId) {
            return (
                <div className={`story-swipe ${murphy.color}-bg`}>
                    <div className="transition-background" style={{ backgroundImage: `url(${murphy.transitionBackground.url })` }} />
                    <div className="character-swipe-name">
                        <div>{murphy.firstName} {murphy.lastName}</div>
                        <div className="title-stroke">
                            <Squiggle />
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }

    const handleSlideChange = (idx) => {
        let slides = document.querySelectorAll('.slick-slide')
        slides.forEach(slide => {
            slide.classList.remove('slide-prev', 'slide-next', 'slide-current')
        })
        let newSlide = document.querySelectorAll(`[data-index='${idx}']`)[0]
        newSlide.classList.add('slide-current')
        newSlide.previousSibling.classList.add('slide-prev')
        newSlide.nextSibling.classList.add('slide-next')
    }

    let settings = {
        className: 'center',
        centerMode: true,
        infinite: true,
        centerPadding: '20%',
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        nextArrow: <CarouselNextArrow />,
        prevArrow: <CarouselPrevArrow />,
        beforeChange: (current, next) => {
            // NOTE: If we use slickGoTo it doesnt call the afterChange event. It also has a race condition which removes the classes before the slide is actually changed.
            if (manualChange) {
                setTimeout(() => {
                    handleSlideChange(next)
                    setManualChange(false)
                }, 500)
            }
        },
        afterChange: current => {
            // remove all prev and next classes from the dom
            handleSlideChange(current)
            let activeMurphy = murphys[current]
            if (activeCharacterId !== activeMurphy.uuid) {
                setActiveCharacterId(activeMurphy.uuid)
            }
            setActiveSlide(current)
        }
    }

    if (uiDeviceType === 'mobile' || uiDeviceType === 'tablet' || uiDeviceType === 'intermediate') {
        return (
            <div className={`murphy-carousel-container active`}>
                <Slider
                    ref={slider => {
                        sliderRef = slider
                    }}
                    {...settings}
                >
                    {renderMurphysCarousel()}
                </Slider>
                {renderCharacterStartButton()}
            </div>
        )
    }

    return (
        <>
            { activeCharacterId &&
                <div className="breadcrumb murphys">
                    <div onClick={() => setActiveCharacterId(null)}>Tales from the East Side</div>
                </div>
            }
            <div className={`murphy-container ${activeCharacterId || isUnloading ? 'inactive' : 'active'}`}>
                <div className="murphy-helper-text">
                    Select a character to start exploring
                </div>
                <div className="container">
                    {renderMurphys()}
                </div>
                {renderStorySwipe()}
            </div>
            <div className={`murphy-carousel-container ${activeCharacterId && !isUnloading ? 'active' : 'inactive'}`}>
                <div className="murphy-helper-text">
                        Use the arrows or swipe to meet people of Clayville
                </div>
                <Slider
                    ref={slider => {
                        sliderRef = slider
                    }}
                    {...settings}
                >
                    {renderMurphysCarousel()}
                </Slider>
                {renderCharacterStartButton()}
            </div>
        </>
    )
}

export default connect(mapStateToProps)(MurphysIndex)
